import { useCallback, useContext, useEffect, useState } from 'react';
import * as R from 'ramda';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import { RequiredDeclarations } from '../../providers/GatekeeperNetworkService/GatekeeperNetworkServiceData';
import { getGknValue } from '../../providers/GatekeeperNetworkService/utils';
import { GatekeeperNetworkServiceContext } from '../../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';

const useTermsLinks = () => {
  const [termsLink, setTermsLink] = useState<string>('');
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState<string>('');
  const [biometricPolicyLink, setBiometricPolicyLink] = useState<string | undefined>(undefined);

  const gknContext = useContext(GatekeeperNetworkServiceContext);
  if (!gknContext) {
    throw new Error('useTermsLinks must be used within a GatekeeperNetworkServiceContext');
  }
  const flowConfigContext = useContext(FlowConfigContext);
  if (!flowConfigContext) {
    throw new Error('useTermsLinks must be used within a FlowConfigContext');
  }
  const {
    initialized,
    setAcceptedTermsAndConditionsLink,
    setAcceptedPrivacyPolicyLink,
    setAcceptedBiometricsLink,
    acceptedTermsAndConditionsLink,
    acceptedPrivacyPolicyLink,
    acceptedBiometricsLink,
  } = flowConfigContext;

  const fetchLinks = useCallback(() => {
    const requiredDeclarations = getGknValue<RequiredDeclarations>(gknContext.gknServiceData!, 'requiredDeclarations');
    if (requiredDeclarations) {
      setTermsLink(requiredDeclarations.acceptedTermsAndConditionsLink as string);
      setPrivacyPolicyLink(requiredDeclarations.acceptedPrivacyPolicyLink as string);
      if (!R.isNil(requiredDeclarations.acceptedBiometricsPolicyLink as string)) {
        setBiometricPolicyLink(requiredDeclarations.acceptedBiometricsPolicyLink as string);
      }
    }
  }, [gknContext.gknServiceData]);

  useEffect(() => {
    if (!initialized) {
      return;
    }
    fetchLinks();
  }, [initialized, fetchLinks]);

  // Terms & conditions and privacy policy links are set based on the same type since they are controlled by the same checkbox.
  const setAcceptedAllTerms = () => {
    setAcceptedTermsAndConditionsLink(termsLink);
    setAcceptedPrivacyPolicyLink(privacyPolicyLink);
    if (!R.isNil(biometricPolicyLink)) {
      setAcceptedBiometricsLink(biometricPolicyLink);
    }
  };

  const hasAcceptedAllTerms = useCallback(() => {
    if (biometricPolicyLink) {
      return acceptedTermsAndConditionsLink && acceptedPrivacyPolicyLink && acceptedBiometricsLink;
    }
    return acceptedTermsAndConditionsLink && acceptedPrivacyPolicyLink;
  }, [acceptedTermsAndConditionsLink, acceptedPrivacyPolicyLink, acceptedBiometricsLink, biometricPolicyLink]);

  return { termsLink, privacyPolicyLink, biometricPolicyLink, setAcceptedAllTerms, hasAcceptedAllTerms };
};

export default useTermsLinks;
